import { useNavigate } from 'react-router-dom';
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import useSubscriptionStore from '../../stores/subscriptionStore';
import { PastDueBanner } from './GracePeriodBanner';

/**
 * TrialBanner — displays trial status information.
 *
 * Shows different banners depending on state:
 * - Active trial: days remaining with upgrade CTA
 * - Expired trial: upgrade required message
 * - Active subscription: nothing (hidden)
 * - Past due: payment failed, with the suspension date and the deletion warning
 *   (yields to GracePeriodBanner while the grace window is live)
 */
const TrialBanner = () => {
  const navigate = useNavigate();
  const {
    trialActive,
    trialExpired,
    trialDaysRemaining,
    trialEndsAt,
    subscriptionStatus,
    hasActiveAccess,
    isInGracePeriod,
    gracePeriodEndsAt,
    suspensionExempt,
  } = useSubscriptionStore();

  // Don't show for active paid subscriptions
  if (subscriptionStatus === 'active' && !trialActive) {
    return null;
  }

  // Active trial
  if (trialActive && trialDaysRemaining > 0) {
    const urgency =
      trialDaysRemaining <= 2
        ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
        : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800';
    const textColor =
      trialDaysRemaining <= 2 ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200';
    const IconComponent = trialDaysRemaining <= 2 ? ExclamationTriangleIcon : ClockIcon;

    const endsDate = trialEndsAt ? new Date(trialEndsAt).toLocaleDateString() : '';

    return (
      <div className={`${urgency} border rounded-lg px-4 py-3 mb-4 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <IconComponent className={`w-5 h-5 ${textColor}`} />
          <div>
            <p className={`text-sm font-medium ${textColor}`}>
              {trialDaysRemaining === 1
                ? 'Your free trial ends tomorrow'
                : `${trialDaysRemaining} days remaining in your free trial`}
            </p>
            {endsDate && <p className={`text-xs ${textColor} opacity-75`}>Trial ends on {endsDate}</p>}
          </div>
        </div>
        <button
          onClick={() => navigate('/subscription')}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
        >
          Upgrade Now
        </button>
      </div>
    );
  }

  // Past due / payment issue. Checked BEFORE the expired-trial branch: the server keeps
  // reporting trial_expired for a seller who trialled, converted and then failed a payment
  // (trial_ends_at and trial_consumed are never cleared), and "your free trial has expired"
  // is false for someone who pays. While the grace window is live the separately mounted
  // GracePeriodBanner speaks for it, so this branch stays quiet rather than stacking a
  // second identical notice.
  if (subscriptionStatus === 'past_due') {
    return isInGracePeriod ? null : (
      <PastDueBanner gracePeriodEndsAt={gracePeriodEndsAt} suspensionExempt={suspensionExempt} />
    );
  }

  // Expired trial
  if (trialExpired || subscriptionStatus === 'expired') {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">Your free trial has expired</p>
            <p className="text-xs text-red-600 dark:text-red-400">
              Upgrade to a paid plan to continue using CopilotPrinter features.
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/subscription')}
          className="px-4 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
        >
          Choose a Plan
        </button>
      </div>
    );
  }

  // Canceled but still active until period end
  if (subscriptionStatus === 'canceled' && hasActiveAccess) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClockIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Your subscription has been canceled. Access continues until the end of your billing period.
          </p>
        </div>
        <button
          onClick={() => navigate('/subscription')}
          className="px-4 py-1.5 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 transition-colors"
        >
          Resubscribe
        </button>
      </div>
    );
  }

  return null;
};

export default TrialBanner;
